import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, TemplateRef, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { merge, of as observableOf, Subject } from "rxjs";
import { catchError, map, startWith, switchMap, takeUntil, delay, filter, tap } from "rxjs/operators";
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { SocialPlatformI } from '../../../interfaces/core.interface';
import { CoreService } from '../../../services/core.service';
import { DialogComponent } from '../../dialog/dialog.component';
import { SocialAiPlatformFormComponent } from '../../forms/create-account/platform-form/platform-form.component';

type ApiResponse<T> = {
  data: T[];
  total: number;
  offset?: number;
  limit?: number;
};

@Component({
  selector: 'app-social-ai-admin-platforms-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginator,
    MatIcon,
    MatToolbarModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    MatTabsModule,
    SocialAiPlatformFormComponent,
    DialogComponent,
  ],
  templateUrl: './social-ai-admin-platforms-table.component.html',
  styleUrl: './social-ai-admin-platforms-table.component.scss',
})
export class SocialAiAdminPlatformsTableComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private _companyID: string | null = null;
  private companyIdStream$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  @Input()
  set companyID(id: string | null) {
    if (this._companyID !== id) {
      this._companyID = id;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.companyIdStream$.next();
      // this.checkIfCanAddMorePlatforms();
    }
  }

  get companyID(): string | null {
    return this._companyID;
  }

  // @Input() formData: any;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('socialPlatformTmpl', { static: true }) socialPlatformTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeletePlatformTmpl', { static: true }) confirmDeletePlatformTmpl!: TemplateRef<any>;

  selectedPlaform: SocialPlatformI = {} as SocialPlatformI;
  dataSource = new MatTableDataSource<SocialPlatformI>();
  displayedColumns: string[] = [
    'id',
    'platform',
    'Domain',
    'isApproved',
    'actions'
  ];

  totalItems: number = 0;
  private AllowNewSocialPlatform: boolean = true;

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // this.checkIfCanAddMorePlatforms();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.paginator.pageIndex = 0;
    });
    this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe((e) => {
    });
    this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe((e) => {
    });

    merge(
      this.sort.sortChange,
      this.paginator.page,
      this.companyIdStream$.asObservable()
    )
      .pipe(
        takeUntil(this.destroy$),
        delay(0),
        filter(() => this._companyID !== null),
        startWith({}),
        switchMap(() => this.getPlatformsDataObservable()),
        catchError(() => observableOf({ data: [], total: 0 } as ApiResponse<SocialPlatformI>))
      )
      .subscribe((res) => {
        this.dataSource.data = res.data ?? [];
        this.totalItems = Number(res.total ?? 0);
      });

    if (this._companyID !== null) {
      this.companyIdStream$.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getPlatformsDataObservable() {
    const pageIndex = this.paginator?.pageIndex ?? 0;
    const pageSize = this.paginator?.pageSize ?? 3; // match your HTML default

    const params: any = {
      sortBy: this.sort?.active ?? '',
      sortOrder: this.sort?.direction ?? '',
      offset: pageIndex * pageSize,
      limit: pageSize,
    };

    if (this.companyID) {
      params.companyID = Number(this.companyID);
    } else {
      console.warn('getPlatformsDataObservable called without companyID.');
      return observableOf({ data: [], total: 0 } as ApiResponse<SocialPlatformI>);
    }

    return this.coreService.getData('/getPlatformsPerCompany', params).pipe(
      catchError((error) => {
        console.error('Error fetching platforms:', error);
        return observableOf({ data: [], total: 0 } as ApiResponse<SocialPlatformI>);
      }),
      map((response: any) => {
        return {
          data: response?.data ?? [],
          total: Number(response?.total ?? 0),
          offset: response?.offset,
          limit: response?.limit,
        } as ApiResponse<SocialPlatformI>;
      })
    );
  }

  addNewPlatformToSite() {
    if (this.companyID === null) {
      this.coreService.showSnackbar("Please select a company first to add a platform.");
      return;
    }

    if (this.AllowNewSocialPlatform) {
      this.selectedPlaform = {
        companyID: Number(this.companyID)
      } as SocialPlatformI;

      this.dialog.open(DialogComponent, {
        width: '760px',
        maxWidth: '95vw',
        data: {
          content: this.socialPlatformTmpl,
          header: 'Add Platform',
          cls: '!bg-slate-600 !text-white'
        }
      });
    } else {
      this.coreService.showSnackbar(
        `You have exceeded the maximum number of social platforms allowed by your economic plan.`
      );
    }
  }

  editPlatform(row: SocialPlatformI): void {
    if (this.companyID === null) {
      this.coreService.showSnackbar("Company ID is not set. Cannot edit platform.");
      return;
    }
    this.selectedPlaform = row;

    this.dialog.open(DialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        content: this.socialPlatformTmpl,
        header: "Edit Platform",
        cls: "!bg-slate-600 !text-white",
      }
    });
  }

  openDeletePlatformConfirm(row: SocialPlatformI) {
    this.selectedPlaform = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeletePlatformTmpl,
        header: 'Delete Platform?',
        cls: 'bg-red-500 !text-white'
      }
    });
  }

  deletePlatform(): void {
    this.coreService.deleteData('deletePlatform', String(this.selectedPlaform.id)).pipe(
      takeUntil(this.destroy$)
    )
      .subscribe(() => {
        this.onSubmitClbk();
      });
  }

  onStatusChange(element: SocialPlatformI): void {
    if (element.isApproved === 'Approved') {
      this.setApprovestatus(element);
    } else if (element.isApproved === 'Not Approved') {
      this.setNotApprovedStatus(element);
    }
  }

  setApprovestatus(row: SocialPlatformI): void {

    this.coreService.postData('socialPlatform/approve', { ID: row.id }).subscribe({
      next: (data) => {

        this.onSubmitClbk();
      },
      error: (error) => {
        console.error("Error updating approval status:", error);
      }
    });
  }

  setNotApprovedStatus(row: SocialPlatformI): void {
    this.coreService.postData('socialPlatform/decline', { ID: row.id }).subscribe({
      next: (data) => {
        this.onSubmitClbk();
      },
      error: (error) => {
        console.error("Error updating approval status:", error);
      }
    });
  }

  onSubmitClbk(): void {
    this.dialog.closeAll();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.companyIdStream$.next();
  }
}
